import { useState, useEffect } from 'react';
import { ChevronLeft } from 'lucide-react';
import { useWorkOrdersStore } from '../../store/workOrdersStore';
import { WorkOrderCard } from './WorkOrderCard';
import { WorkOrderDetail } from './WorkOrderDetail';
import { EmptyState } from '../common/EmptyState';

interface WorkOrderListProps {
  showCompleted?: boolean;
}

export function WorkOrderList({ showCompleted = false }: WorkOrderListProps) {
  const { data, selectedAddress, setSelectedAddress } = useWorkOrdersStore();
  const [selectedWorkOrder, setSelectedWorkOrder] = useState<number | null>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [selectedWorkOrder]);

  const orders = data
    .filter((order) => order.address === selectedAddress)
    .filter((order) => 
      showCompleted 
        ? ['COMPLETED', 'REJECTED'].includes(order.status)
        : !['COMPLETED', 'REJECTED'].includes(order.status)
    )
    .sort((a, b) => b.id - a.id); // Sort by newest first

  const selectedOrderDetails = orders.find(order => order.id === selectedWorkOrder);

  if (selectedOrderDetails) {
    return (
      <WorkOrderDetail
        workOrder={selectedOrderDetails}
        onBack={() => setSelectedWorkOrder(null)}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => setSelectedAddress(null)}
          className="p-2 hover:bg-gray-100 dark:hover:bg-dark-border rounded-lg transition-colors"
        >
          <ChevronLeft className="h-6 w-6 text-gray-600 dark:text-gray-400" />
        </button>
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            {selectedAddress}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {orders[0]?.city}, {orders[0]?.state}
          </p>
        </div>
      </div>

      {orders.length === 0 ? (
        <EmptyState 
          message={showCompleted 
            ? "No completed work orders for this address" 
            : "No active work orders for this address"
          } 
        />
      ) : (
        <div className="space-y-4">
          {orders.map((order) => (
            <WorkOrderCard
              key={order.id}
              workOrder={order}
              onClick={() => setSelectedWorkOrder(order.id)}
              isCompleted={showCompleted}
            />
          ))}
        </div>
      )}
    </div>
  );
}